import { ChangeDetectionStrategy, Component, OnInit, model, ViewEncapsulation, computed, inject, input } from '@angular/core';
import { FiRemoteStateFullDataSource } from '@fi-shared/fi-items-list/fi-remote-state-full-data-source';
import { ITask2ReportAvailableItems, ITask2ReportItem, ITask2ReportParameter, ITask2ReportWidget, ITask2ReportWidgetFilter } from '../../../fi-task-v2-report';
import { FiItemsListType } from '@fi-app/shared/fi-items-list/fi-items-list-type';
import { ObjectTypes } from '@fi-app/shared/types';
import { isEqual } from '@fi-shared/utils';
import { FiTaskReportType, FiTaskReportDateType } from '@fi-app/task-v2/fi-task-v2-report/fi-task-v2-report-enums';
import { Sort } from '@angular/material/sort';
import { MatSort } from '@angular/material/sort';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { IItemsListDataFetchResult } from '@fi-shared/fi-items-list/fi-items-list';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs';
import { FiTaskV2ReportService } from '../../../services/fi-task-v2-report.service';

@Component({
    selector: 'fi-task-v2-report-widget-filter-settings-content',
    templateUrl: './widget-filter-settings-content.component.html',
    styleUrls: ['./widget-filter-settings-content.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-task-v2-report-widget-filter-settings-content' },
    standalone: false,
})
export class FiTaskV2ReportWidgetFilterSettingsContentComponent extends FiRemoteStateFullDataSource<ITask2ReportItem> implements OnInit {
    currentFilter = model.required<ITask2ReportWidgetFilter>();
    reportWidget = model.required<ITask2ReportWidget>();    
    availableItems = input.required<ITask2ReportAvailableItems>();

    private fiTaskV2ReportService = inject(FiTaskV2ReportService);

    defaultSortColumnName: string = 'Name';
    defaultSortDirection: boolean;
    itemsListType = FiItemsListType.TaskV2ReportWidget;
    objectType = ObjectTypes.Task2Reports;
    private selectedFilterItemsChanged$ = new Subject<void>();

    private initialFilter: ITask2ReportWidgetFilter = {};

    protected overdueReport = computed(() => this.reportWidget().ReportType === FiTaskReportType.Overdue);

    protected get filterChanged(): boolean {
        return !isEqual(this.currentFilter(), this.initialFilter);
    }

    ngOnInit(): void {
        this.subscribeOnFilterItemsChange();
    }
    
    protected onSelectedItemsChanged(newFilterData: ITask2ReportWidgetFilter): void {
        this.currentFilter.set({ ...newFilterData });
        this.selectedFilterItemsChanged$.next();
    }

    public onSortChanged($event: Sort): void {
        this.matSort = { active: $event.active, direction: $event.direction } as MatSort;
        super.onSortChanged($event);
    }

    loadListDataFn(loadArgs: ILoadDataArgs): Promise<IItemsListDataFetchResult<ITask2ReportItem>> {
        const parameter = this.getReportParameter(loadArgs);
        const promise = new Promise<IItemsListDataFetchResult<ITask2ReportItem>>((resolve, reject) => {
            this.fiTaskV2ReportService.getReportData(parameter).then((reportData) => {
                resolve({
                    Items: reportData.Tasks,
                    TotalCount: undefined,
                    LightWeightObjectsForLocalSearch: undefined,
                });
            }, reject);
        });
        return promise;
    }

    private getReportParameter(loadArgs: ILoadDataArgs): ITask2ReportParameter {
        const parameter = super.getItemsListParameter(loadArgs) as ITask2ReportParameter;

        parameter.ReportType = this.reportWidget().ReportType;
        parameter.Filter = this.currentFilter();
        parameter.DateRange = { TimePeriodType: FiTaskReportDateType.PastQuarter };
        parameter.LoadSummary = !loadArgs.fetchOnlyItems;
        parameter.LoadAvailableItems = !loadArgs.fetchOnlyItems;

        return parameter;
    }

    protected setSortData() {
        super.setSortData();
        if (!this.overdueReport()) {
            return;
        }

        this.sortColumn = this.defaultSortColumnName;
    }
    
    private subscribeOnFilterItemsChange(): void {
        this.selectedFilterItemsChanged$
            .pipe(debounceTime(500), takeUntil(this.ngOnDestroy$))
            .subscribe(() => this.reloadData());
    }

    protected resetCurrentFilter(): void {
        this.onSelectedItemsChanged({});
    }
}